import { ComponentType } from 'react';

import capitalize from 'lodash/capitalize';

import { Flex, Tag, Text } from '@spotify-internal/encore-web';
import {
  DemoTag,
  StatusOKMui as StatusOK,
  StatusPendingMui as StatusPending,
  StatusRunningMui as StatusRunning,
  StatusWarningMui as StatusWarning,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';
import {
  FlagsAdminV1SegmentState,
  Maybe,
  isType,
} from '@spotify/confidence-graphql';
import { useSetResolvedTitle } from '@spotify/plugin-confidence-projects-react';
import { useSegment } from '@spotify/plugin-confidence-targeting-react';

import { PreviewShell } from './components';

interface SegmentArtifactPreviewProps {
  resourceName: string;
}

const STATE_DOTS: Record<FlagsAdminV1SegmentState, ComponentType> = {
  [FlagsAdminV1SegmentState.Allocated]: StatusOK,
  [FlagsAdminV1SegmentState.Archived]: StatusPending,
  [FlagsAdminV1SegmentState.Unallocated]: StatusRunning,
  [FlagsAdminV1SegmentState.Ok]: StatusOK,
};

const StateIndicator = ({
  state,
}: {
  state?: Maybe<FlagsAdminV1SegmentState>;
}) => {
  const Dot = state ? STATE_DOTS[state] : StatusWarning;
  return (
    <Flex alignItems="center" gap="tighter-2">
      <Dot />
      <Text variant="bodySmall" semanticColor="textSubdued">
        {capitalize(state ?? 'unknown')}
      </Text>
    </Flex>
  );
};

const formatAllocation = (proportion: unknown): string | undefined => {
  if (typeof proportion !== 'string' && typeof proportion !== 'number') {
    return undefined;
  }
  const value = Number(proportion);
  if (!Number.isFinite(value)) return undefined;
  return `${Math.round(value * 100)}%`;
};

export const SegmentArtifactPreview = ({
  resourceName,
}: SegmentArtifactPreviewProps) => {
  const { segment, isLoading, error } = useSegment({ name: resourceName });

  const linkedFlag = isType(segment?.flag, 'FlagsAdminV1Flag')
    ? segment.flag
    : undefined;
  const previewError =
    error ?? (!isLoading && !segment ? 'not-found' : undefined);

  useSetResolvedTitle(
    segment?.displayName || extractLastNameComponent(resourceName),
  );

  const allocation = formatAllocation(segment?.allocation?.proportion?.value);

  return (
    <PreviewShell loading={isLoading} error={previewError}>
      {segment && (
        <Flex direction="column" gap="tighter-2">
          <Flex direction="column" gap="tighter-4">
            <Flex alignItems="center" gap="tighter-2">
              <Text variant="bodyMediumBold">
                {segment.displayName ||
                  extractLastNameComponent(segment.name) ||
                  segment.name}
              </Text>
              <DemoTag labels={segment.labels} />
            </Flex>
            <StateIndicator state={segment.state} />
          </Flex>
          {segment.description && (
            <Text variant="bodySmall">{segment.description}</Text>
          )}
          {allocation && (
            <Flex alignItems="center" gap="tighter-2">
              <Text variant="bodySmall">Allocation:</Text>
              <Text variant="bodySmall" semanticColor="textSubdued">
                {allocation}
              </Text>
            </Flex>
          )}
          {linkedFlag &&
            (() => {
              const flagLabel =
                linkedFlag.displayName ||
                extractLastNameComponent(linkedFlag.name) ||
                linkedFlag.name;
              return (
                <Flex alignItems="center" gap="tighter-2">
                  <Text variant="bodySmall">Flag:</Text>
                  <Flex wrap="wrap" gap="tighter-2" aria-label="Flag">
                    <Tag>{flagLabel}</Tag>
                  </Flex>
                </Flex>
              );
            })()}
        </Flex>
      )}
    </PreviewShell>
  );
};
